import {BulbOutlined, CameraOutlined, QrcodeOutlined} from "@ant-design/icons";
import {Button, Modal, Space, Spin, Typography, message} from "antd";
import QrScanner from "qr-scanner";
import React, {useEffect, useRef, useState} from "react";
import styles from "./qrCameraScannerFab.module.less";

type QrCameraScannerFabProps = {
    isEnabled: boolean;
    onScan: (data: string) => Promise<void> | void;
};

export default function QrCameraScannerFab({isEnabled, onScan}: QrCameraScannerFabProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasCamera, setHasCamera] = useState<boolean | null>(null);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState("Uruchamianie kamery...");
    const [isTorchAvailable, setIsTorchAvailable] = useState(false);
    const [isTorchEnabled, setIsTorchEnabled] = useState(false);
    const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
    const scannerRef = useRef<QrScanner | null>(null);
    const isHandlingScanRef = useRef(false);

    useEffect(() => {
        let isCancelled = false;

        void QrScanner.hasCamera()
            .then(cameraAvailable => {
                if (!isCancelled) {
                    setHasCamera(cameraAvailable);
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setHasCamera(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }

        let isCancelled = false;

        if (!videoElement) {
            return;
        }

        const initializeScanner = async () => {
            setErrorMessage(null);
            setIsCameraReady(false);
            setIsTorchAvailable(false);
            setIsTorchEnabled(false);
            setStatusMessage("Prośba o dostęp do kamery...");

            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new Error("MEDIA_DEVICES_UNAVAILABLE");
                }

                if (!window.isSecureContext) {
                    throw new Error("INSECURE_CONTEXT");
                }

                const previewStream = await withTimeout(
                    navigator.mediaDevices.getUserMedia({
                        video: {
                            facingMode: {ideal: "environment"}
                        },
                        audio: false
                    }),
                    10000
                );

                previewStream.getTracks().forEach(track => track.stop());
                setStatusMessage("Uruchamianie podglądu kamery...");

                const scanner = new QrScanner(
                    videoElement,
                    async result => {
                        if (isHandlingScanRef.current) {
                            return;
                        }

                        isHandlingScanRef.current = true;

                        try {
                            await onScan(result.data);
                            setIsModalOpen(false);
                        } finally {
                            isHandlingScanRef.current = false;
                        }
                    },
                    {
                        preferredCamera: "environment",
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        returnDetailedScanResult: true,
                        onDecodeError: error => {
                            if (String(error) !== QrScanner.NO_QR_CODE_FOUND && !isCancelled) {
                                setErrorMessage("Skaner napotkał problem z odczytem obrazu z kamery.");
                            }
                        }
                    }
                );

                scannerRef.current = scanner;
                await withTimeout(scanner.start(), 10000);

                if (isCancelled) {
                    scanner.destroy();
                    return;
                }

                setIsCameraReady(true);
                setErrorMessage(null);

                const flashAvailable = await scanner.hasFlash().catch(() => false);
                if (!isCancelled) {
                    setIsTorchAvailable(flashAvailable);
                }
            } catch (error: any) {
                if (!isCancelled) {
                    setErrorMessage(getCameraErrorMessage(error));
                }
            }
        };

        void initializeScanner();

        return () => {
            isCancelled = true;
            scannerRef.current?.destroy();
            scannerRef.current = null;
            isHandlingScanRef.current = false;
            setIsCameraReady(false);
            setIsTorchAvailable(false);
            setIsTorchEnabled(false);
        };
    }, [isModalOpen, onScan, videoElement]);

    const handleTorchToggle = async () => {
        if (!scannerRef.current) {
            return;
        }

        try {
            await scannerRef.current.toggleFlash();
            setIsTorchEnabled(scannerRef.current.isFlashOn());
        } catch {
            message.warning("Nie udało się zmienić stanu lampy.");
        }
    };

    if (!isEnabled || hasCamera !== true) {
        return null;
    }

    return <>
        <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<QrcodeOutlined />}
            className={styles.fabButton}
            onClick={() => setIsModalOpen(true)}
        />
        <Modal
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            title="Skaner kodów QR"
            footer={[
                isTorchAvailable ? (
                    <Button key="torch" icon={<BulbOutlined />} onClick={() => void handleTorchToggle()}>
                        {isTorchEnabled ? "Wyłącz lampę" : "Włącz lampę"}
                    </Button>
                ) : null,
                <Button key="close" onClick={() => setIsModalOpen(false)}>
                    Zamknij
                </Button>
            ]}
            destroyOnHidden
            width={560}
            className={styles.scannerModal}
        >
            <div className={styles.scannerLayout}>
                <div className={styles.scannerFrame}>
                    <video
                        ref={setVideoElement}
                        className={`${styles.video} ${!isCameraReady ? styles.videoHidden : ""}`.trim()}
                        muted
                        playsInline
                    />
                    {!isCameraReady && (
                        <div className={styles.statusOverlay}>
                            <Space direction="vertical" size="middle" className={styles.statusCard}>
                                {!errorMessage && <>
                                    <Spin size="large" />
                                    <Typography.Text style={{color: "inherit"}}>
                                        {statusMessage}
                                    </Typography.Text>
                                </>}
                                {errorMessage && <>
                                    <CameraOutlined style={{fontSize: 28}} />
                                    <Typography.Text style={{color: "inherit"}}>
                                        {errorMessage}
                                    </Typography.Text>
                                </>}
                            </Space>
                        </div>
                    )}
                </div>
                <Typography.Text type="secondary" className={styles.helperText}>
                    Skieruj kamerę na kod QR
                </Typography.Text>
            </div>
        </Modal>
    </>;
}

function getCameraErrorMessage(error: Error | {name?: string; message?: string} | string) {
    const errorName = typeof error === "string" ? error : error?.name;
    const errorMessage = typeof error === "string" ? error : error?.message;

    if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
        return "Dostęp do kamery został zablokowany. Zezwól na kamerę w przeglądarce i otwórz skaner ponownie.";
    }

    if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
        return "Nie wykryto kamery w tym urządzeniu.";
    }

    if (errorName === "NotReadableError" || errorName === "TrackStartError") {
        return "Kamera jest zajęta przez inną aplikację albo przeglądarkę.";
    }

    if (errorMessage === "INSECURE_CONTEXT") {
        return "Skaner kamery działa tylko w bezpiecznym kontekście, np. na localhost albo HTTPS.";
    }

    if (errorMessage === "MEDIA_DEVICES_UNAVAILABLE") {
        return "Ta przeglądarka nie udostępnia API kamery wymaganej do skanowania QR.";
    }

    if (errorMessage === "CAMERA_START_TIMEOUT") {
        return "Uruchomienie kamery trwało zbyt długo. Problem wygląda na zawieszenie podczas startu streamu albo `qr-scanner`.";
    }

    return "Nie udało się uruchomić kamery. Sprawdź uprawnienia przeglądarki.";
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number) {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
        return await Promise.race([
            promise,
            new Promise<never>((_, reject) => {
                timeoutId = setTimeout(() => reject(new Error("CAMERA_START_TIMEOUT")), timeoutMs);
            })
        ]);
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
}
